import { averageBy } from './chartUtils';
import { appPrimaryColor, scatterColor, getCategoryColor } from './colors';
import { IExpenseCategory, IExpenseCategoryData } from './types';
import {
  ResponsiveContainer,
  ComposedChart,
  Scatter,
  CartesianGrid,
  XAxis,
  YAxis,
  ReferenceLine,
  Line,
  Tooltip,
  Bar,
  Legend,
} from 'recharts';
import ExpenseCategoryTooltip from './ExpenseCategoryTooltip';
import { Container, Row, Col, Form } from 'react-bootstrap';
import { ICategory } from '../../../../interfaces/ICategory';
import { useState } from 'react';
import { useLocalStorage } from 'react-use';

interface ExpenseCategoryChartProps {
  data: IExpenseCategoryData[];
  allCategories: ICategory[];
  showReferenceLines: boolean | undefined;
  chartHeight: number;
  includeTaxes?: boolean;
}

const ExpenseCategoryChart = ({
  data,
  allCategories,
  showReferenceLines,
  chartHeight,
  includeTaxes,
}: ExpenseCategoryChartProps) => {
  const [showExpenseLine, setShowExpenseLine] = useLocalStorage<boolean>('ExpenseCategoryChart.showExpenseLine', true);
  const [showExpenseBar, setShowExpenseBar] = useLocalStorage<boolean>('ExpenseCategoryChart.showExpenseBar', true);
  const [selectedCategoryPath, setSelectedCategoryPath] = useLocalStorage<number[]>(
    'ExpenseCategoryChart.selectedCategoryPath',
    []
  );

  if (!selectedCategoryPath) {
    return;
  }

  const [hoveredCategoryId, setHoveredCategoryId] = useState<number | null>(null);

  const RenderLegend = () => {
    const selects: Array<{ options: IExpenseCategory[]; selectedId?: number; level: number; parentId?: number }> = [];
    let parentId: number | undefined = undefined;

    const isChildOf = (category: IExpenseCategory, parentId: number | undefined) => {
      if (parentId === undefined) {
        return (
          category.parentCategoryId === undefined ||
          category.parentCategoryId === null ||
          category.parentCategoryId === -1
        );
      }
      return category.parentCategoryId === parentId;
    };

    while (true) {
      const level = selects.length + 1;
      const options = categories.filter(
        (category) => category.categoryLevel === level && isChildOf(category, parentId)
      );
      if (options.length === 0) {
        break;
      }

      const selectedId = selectedCategoryPath[selects.length];
      selects.push({ options, selectedId, level, parentId });

      if (selectedId === undefined) {
        break;
      }

      parentId = selectedId;
    }

    const handleSelectChange = (index: number, value: string) => {
      const nextCategoryPath = selectedCategoryPath.slice(0, index);
      if (value !== '') {
        nextCategoryPath.push(Number(value));
      }
      setSelectedCategoryPath(nextCategoryPath);
    };

    return (
      <Container>
        <Row className='justify-content-center align-items-start' style={{ gap: '16px', paddingBottom: '5px' }}>
          <Col xs='auto'>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <Form.Check
                type='checkbox'
                label='Wyświetl wydatki'
                id='show-expense-line-expense-category-chart-checkbox'
                checked={showExpenseLine}
                onChange={() => setShowExpenseLine(!showExpenseLine)}
                style={{ fontSize: '12px', userSelect: 'none' }}
              />
              <Form.Check
                type='checkbox'
                label='Wyświetl szczegóły '
                id='show-expense-bar-expense-category-chart-checkbox'
                checked={showExpenseBar}
                onChange={() => setShowExpenseBar(!showExpenseBar)}
                style={{ fontSize: '12px', userSelect: 'none' }}
              />
            </div>
          </Col>
          {selects.map((select, index) => (
            <Col xs='auto'>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <div key={select.level} style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <label style={{ fontSize: '12px', fontWeight: 'bold' }}>Kategoria {select.level}</label>
                  <Form.Select
                    value={select.selectedId ?? ''}
                    onChange={(e) => handleSelectChange(index, e.target.value)}
                    style={{
                      width: '120px',
                      height: '25px',
                      fontSize: '12px',
                      padding: '2px 4px',
                    }}
                  >
                    <option value={''}>{index === 0 ? 'Wszystkie' : 'Wybierz'}</option>
                    {select.options.map((category) => (
                      <option key={category.categoryId} value={category.categoryId}>
                        {category.categoryName}
                      </option>
                    ))}
                  </Form.Select>
                </div>
              </div>
            </Col>
          ))}
        </Row>
      </Container>
    );
  };

  const aggregateCategories = (
    categories: IExpenseCategory[],
    allCategoriesMap: Map<number, IExpenseCategory>
  ): IExpenseCategory[] => {
    const directCosts = new Map<number, number>();
    for (const category of categories) {
      const existingCategory = allCategoriesMap.get(category.categoryId);
      const categoryTotalCost = !includeTaxes && existingCategory?.isTax ? 0 : (category.totalCost ?? 0);
      directCosts.set(category.categoryId, (directCosts.get(category.categoryId) ?? 0) + categoryTotalCost);
    }

    // Include every ancestor so a category without direct expenses can still
    // pass its descendants' total to the top-level category.
    const includedCategoryIds = new Set(directCosts.keys());
    for (const categoryId of directCosts.keys()) {
      let current = allCategoriesMap.get(categoryId);
      const visited = new Set<number>();

      while (
        current?.parentCategoryId !== undefined &&
        current.parentCategoryId !== null &&
        current.parentCategoryId !== -1
      ) {
        if (visited.has(current.categoryId)) {
          console.warn(`Wykryto cykl w hierarchii kategorii dla ${current.categoryId}`);
          break;
        }

        visited.add(current.categoryId);
        const parent = allCategoriesMap.get(current.parentCategoryId);
        if (!parent) {
          console.warn(`Nie znaleziono rodzica ${current.parentCategoryId} dla kategorii ${current.categoryId}`);
          break;
        }

        includedCategoryIds.add(parent.categoryId);
        current = parent;
      }
    }

    const totals = new Map<number, number>();
    const calculateTotal = (categoryId: number, visiting: Set<number> = new Set()): number => {
      const cachedTotal = totals.get(categoryId);
      if (cachedTotal !== undefined) {
        return cachedTotal;
      }

      if (visiting.has(categoryId)) {
        console.warn(`Wykryto cykl w hierarchii kategorii dla ${categoryId}`);
        return directCosts.get(categoryId) ?? 0;
      }

      const nextVisiting = new Set(visiting);
      nextVisiting.add(categoryId);
      let total = directCosts.get(categoryId) ?? 0;

      for (const childId of includedCategoryIds) {
        const child = allCategoriesMap.get(childId);
        if (child?.parentCategoryId === categoryId && childId !== categoryId) {
          total += calculateTotal(childId, nextVisiting);
        }
      }

      totals.set(categoryId, total);
      return total;
    };

    return [...includedCategoryIds].map((categoryId) => {
      const category = allCategoriesMap.get(categoryId);
      if (!category) {
        throw new Error(`Nie znaleziono kategorii ${categoryId}`);
      }

      return {
        ...category,
        // totalCost: aggregated sum including descendants
        totalCost: calculateTotal(categoryId),
        // directCost: only the amount directly assigned to this category
        directCost: directCosts.get(categoryId) ?? 0,
      };
    });
  };

  const allCategoriesMap = new Map<number, IExpenseCategory>();

  allCategoriesMap.set(-1, {
    categoryId: -1,
    categoryName: 'Wszystkie',
    categoryLevel: 0,
    isTax: false,
    parentCategoryId: undefined,
    totalCost: 0,
  });

  allCategories.forEach((category) => {
    if (!allCategoriesMap.has(category.categoryId)) {
      allCategoriesMap.set(category.categoryId, {
        categoryId: category.categoryId,
        categoryName: category.categoryName,
        categoryLevel: category.categoryLevel,
        isTax: category.isTax,
        parentCategoryId: category.parentCategoryId,
        totalCost: 0,
      });
    }
  });

  const chartData = data.map((item) => ({
    ...item,
    expenseCategories: aggregateCategories(item.expenseCategories ?? [], allCategoriesMap),
  }));

  let categories = Array.from(
    new Map(chartData.flatMap((x) => x.expenseCategories ?? []).map((x) => [x.categoryId, x])).values()
  );
  const selectedCategoryId = selectedCategoryPath[selectedCategoryPath.length - 1];

  const visibleCategories = (() => {
    if (selectedCategoryId === undefined) {
      return categories.filter(
        (category) =>
          category.categoryLevel === 1 &&
          (category.parentCategoryId === undefined ||
            category.parentCategoryId === null ||
            category.parentCategoryId === -1)
      );
    }

    const children = categories.filter((category) => category.parentCategoryId === selectedCategoryId);
    if (children.length > 0) {
      const parent = categories.find((category) => category.categoryId === selectedCategoryId);
      return parent ? [parent, ...children] : children;
    }

    return categories.filter((category) => category.categoryId === selectedCategoryId);
  })();

  const sortedVisibleCategories = [...visibleCategories].sort((a, b) => {
    const isViewingSingleCategoryWithChildren =
      selectedCategoryId !== undefined && categories.some((c) => c.parentCategoryId === selectedCategoryId);

    const getDisplayTotal = (categoryId: number) =>
      chartData.reduce((total, item) => {
        const cat = item.expenseCategories?.find((category) => category.categoryId === categoryId);
        if (!cat) return total;
        if (isViewingSingleCategoryWithChildren && categoryId === selectedCategoryId) {
          return total + (cat.directCost ?? 0);
        }
        return total + (cat.totalCost ?? 0);
      }, 0 as number);

    return getDisplayTotal(a.categoryId) - getDisplayTotal(b.categoryId);
  });

  const getVisibleExpense = (item: IExpenseCategoryData) =>
    sortedVisibleCategories.reduce((total, category) => {
      const cat = item.expenseCategories?.find((expense) => expense.categoryId === category.categoryId);
      if (!cat) return total;
      const isViewingSingleCategoryWithChildren =
        selectedCategoryId !== undefined && categories.some((c) => c.parentCategoryId === selectedCategoryId);
      if (isViewingSingleCategoryWithChildren && category.categoryId === selectedCategoryId) {
        return total + (cat.directCost ?? 0);
      }
      return total + (cat.totalCost ?? 0);
    }, 0);
  const chartDataWithVisibleExpense = chartData.map((item) => ({
    ...item,
    visibleExpense: getVisibleExpense(item),
  }));
  const visibleExpenseAverage = averageBy(chartDataWithVisibleExpense, (item) => item.visibleExpense);
  const categoryColors = new Map(
    sortedVisibleCategories.map((category, index) => [category.categoryId, getCategoryColor(index)])
  );

  const isViewingSingleCategoryWithChildren =
    selectedCategoryId !== undefined && categories.some((c) => c.parentCategoryId === selectedCategoryId);

  const buildCategoryPath = (categoryId: number) => {
    const path: number[] = [];
    let current = categories.find((c) => c.categoryId === categoryId);
    if (!current) return [categoryId];
    // walk up to root
    while (
      current &&
      current.parentCategoryId !== undefined &&
      current.parentCategoryId !== null &&
      current.parentCategoryId !== -1
    ) {
      path.push(current.categoryId);
      current = categories.find((c) => c.categoryId === current!.parentCategoryId);
    }
    if (current) path.push(current.categoryId);
    return path.reverse();
  };

  const handleBarClick = (categoryId: number) => {
    const path = buildCategoryPath(categoryId);
    setSelectedCategoryPath(path);
  };

  return (
    <div className='overflow-auto'>
      <ResponsiveContainer minWidth={600} width='100%' height={chartHeight}>
        <ComposedChart
          accessibilityLayer={false}
          data={chartDataWithVisibleExpense}
          margin={{
            left: 10,
            right: 25,
            top: 5,
            bottom: 5,
          }}
        >
          <Scatter key={0} name={'Wydatki'} line={{ strokeWidth: 0.75 }} fill={scatterColor} cursor='pointer' />
          <CartesianGrid strokeDasharray='1' />
          {/* YYYYMMWW - nie mamy normalnej daty, więc trzeba generowac liczbę będącą reprezentacją tygodni i miesięcy */}
          <XAxis
            dataKey={(data: IExpenseCategoryData) => data.year * 10000 + (data.month ?? 0) * 100}
            tickFormatter={(value) => ((value / 100) % 100).toString()}
            name={'Miesiąc'}
          />
          <YAxis width={'auto'} unit=' zł' />
          <div>
            {showExpenseBar &&
              sortedVisibleCategories.map((category) => (
                <Bar
                  key={category.categoryId}
                  dataKey={(item: IExpenseCategoryData) => {
                    const cat = item.expenseCategories?.find((c) => c.categoryId === category.categoryId);
                    if (!cat) return 0;
                    if (isViewingSingleCategoryWithChildren && category.categoryId === selectedCategoryId) {
                      return (cat.directCost ?? 0) > 0 ? cat.directCost : 0;
                    }
                    return (cat.totalCost ?? 0) > 0 ? cat.totalCost : 0;
                  }}
                  stackId='expenses'
                  fill={categoryColors.get(category.categoryId)}
                  fillOpacity={hoveredCategoryId === category.categoryId ? 1 : 0.85}
                  stroke='rgba(255, 255, 255, 0.5)'
                  strokeWidth={hoveredCategoryId === category.categoryId ? 1 : 0.5}
                  style={{ transition: 'fill-opacity 120ms, stroke-width 120ms' }}
                  name={category.categoryName}
                  cursor='pointer'
                  onClick={() => handleBarClick(category.categoryId)}
                  onMouseEnter={() => setHoveredCategoryId(category.categoryId)}
                  onMouseLeave={() => setHoveredCategoryId(null)}
                  onContextMenu={(data: any, index: number, e?: React.MouseEvent) => {
                    e?.preventDefault();
                    setSelectedCategoryPath([]);
                  }}
                />
              ))}
            {showExpenseLine && (
              <Line
                type='monotone'
                dataKey='visibleExpense'
                stroke={appPrimaryColor}
                name='Wydatki'
                unit=' zł'
                activeDot={{ r: 6 }}
              />
            )}
          </div>
          {showReferenceLines && (
            <ReferenceLine
              y={visibleExpenseAverage}
              label={{
                dx: 10,
                dy: -10,
                value: `średnia: ${visibleExpenseAverage.toFixed(2)} zł`,
                style: { fontSize: '14px', fontWeight: 'bold', fill: appPrimaryColor },
              }}
              stroke={appPrimaryColor}
              strokeDasharray='3 3'
            />
          )}
          <Legend content={<RenderLegend />} verticalAlign='top' />
          <Tooltip
            content={(props) => (
              <ExpenseCategoryTooltip
                {...props}
                categories={categories}
                categoryOrder={sortedVisibleCategories.map((category) => category.categoryId)}
                getCategoryColor={(id) => categoryColors.get(id) ?? getCategoryColor(id)}
                selectedCategoryId={selectedCategoryId}
                isViewingSingleCategoryWithChildren={isViewingSingleCategoryWithChildren}
                hoveredCategoryId={hoveredCategoryId}
              />
            )}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ExpenseCategoryChart;
